import { Grid, Card, Typography, Box, Tooltip } from '@mui/material';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useMemo, startTransition, useRef, createRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { enqueueSnackbar } from '../../../../reusable-components/snackbar';
import { cardStyle, selectedTabStyle, formCardStyle } from '../TransitionStyles';
import { getStatusConfig, getReduxDueDiligenceData } from '../../../../redux/slices/transitions';
import { saveDueDiligenceForm } from '../../../../api/transitions';
import FormBuilder from '../FormBuilder';
import SaveButton from '../SaveButton';
import TableOfContents from './TableOfContents';
import { DueDiligenceFormTitles } from '../formTitlesLists';
import { trimData } from '../../../../utils/formatText';
import SkeletonBlock from '../SkeletonBlock';
import AccessDenied from '../../../../reusable-components/accessDenied/AccessDenied';
import UpdateStatusButtons from '../UpdateStatusButtons';
import CanceledAlert from '../CanceledAlert';
import FormsDropdown from '../FormsDropdown';
import BackToHomeButton from '../BackToHomeButton';
import { PAGE_HEIGHT } from '../constants';

export default function DueDiligenceView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState();
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [activeSection, setActiveSection] = useState(0);
  const { id } = useParams();
  const containerRef = useRef();
  const offsets = useRef([]);
  const formRefs = DueDiligenceFormTitles?.map(() => createRef());
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const {
    data: { statusConfig },
  } = useSelector((state) => state.transitions);

  useEffect(() => {
    if (statusConfig == null) dispatch(getStatusConfig());
  }, []);

  const tabIndex = useMemo(() => {
    let idx = 0;
    if (data?.facilityTabs && facilityId) {
      idx = data.facilityTabs?.findIndex((fac) => String(fac.sellerFacilityId) === String(facilityId));
    }

    return idx !== -1 ? idx : 0;
  }, [data]);

  const formData = useMemo(() => {
    if (!data) return undefined;
    const fac = data?.dueDiligenceFields;

    const mappedFields = {
      ...fac.contractsFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.employeeMattersAndBenefitsFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.financialFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.generalGovernanceFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.initialDueDiligenceFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.insuranceAndRiskManagementFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.licensureAndRegulatoryFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.litigationFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      ...fac.physicalPlantAndCapitalExpendituresFields.reduce(
        (acc, field) => ({ ...acc, [field.fieldName]: field.answer }),
        {}
      ),
      ...fac.realEstateFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: field.answer }), {}),
      transitionFacilityId: fac.transitionFacilityId,
    };

    return { ...mappedFields };
  }, [data]);

  const methods = useForm({ defaultValues: formData });

  useEffect(() => {
    setIsLoadingForms(true);
    dispatch(getReduxDueDiligenceData(id, facilityId ?? 0))
      .then((resp) => {
        setData(resp);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoadingForms(false);
        setIsLoadingPage(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    offsets.current = formRefs.map((e) => ({
      top: e?.current?.offsetTop,
      bottom: e?.current?.offsetTop + e?.current?.offsetHeight,
    }));
  }, [formRefs]);

  useEffect(() => {
    if (formData) {
      methods.reset(formData);
    }
  }, [formData]);

  const submitForm = async (allData) => {
    const result = trimData(allData);

    await saveDueDiligenceForm({
      id: data.id,
      sellerFacilityId: data?.dueDiligenceFields?.sellerFacilityId,
      dueDiligenceData: result,
    })
      .then((resp) => {
        enqueueSnackbar('Due Diligence data Saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleTabChange = (index) => {
    if (index === tabIndex) return;

    const facId = data?.facilityTabs[index]?.sellerFacilityId;
    setActiveSection(0);
    if (facId) {
      navigate(
        `/dashboard/transitions/due-diligence/${id}?facilityId=${facId}`,
        { replace: true } //avoids polluting history
      );
    }
  };

  const onScroll = () => {
    //Add 400px to the position to find section in top-middle of viewport
    const scrollPosition = containerRef.current.scrollTop + 400;

    offsets.current.forEach((o, index) => {
      if (scrollPosition >= o.top && scrollPosition < o.bottom) {
        requestAnimationFrame(() => {
          startTransition(() => {
            setActiveSection(index);
          });
        });
      }
    });
  };

  const tabDisabledMessage = (facIdx) => {
    if (tabIndex !== facIdx && methods.formState.isDirty) return 'Save changes before changing tabs';
    return null;
  };

  return (
    <>
      {isLoadingPage ? (
        <Grid container spacing={2}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={2}>
            <SkeletonBlock height={240} />
            <SkeletonBlock height={90} sx={{ mt: 2 }} />
          </Grid>

          {/* MAIN CONTENT */}
          <Grid item xs={12} md={8}>
            <SkeletonBlock height={60} />
            <SkeletonBlock height={520} sx={{ mt: 2 }} />
          </Grid>

          {/* RIGHT PANEL */}
          <Grid item xs={12} md={2}>
            <SkeletonBlock height={120} />
            <SkeletonBlock height={340} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(submitForm)}>
            <BackToHomeButton />
            <Grid sx={selectedTabStyle}>
              <Tabs selectedIndex={tabIndex} onSelect={handleTabChange}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={2}>
                    <TabList style={{ borderBottom: '0' }}>
                      {data?.dueDiligenceFields && (
                        <Card sx={cardStyle}>
                          <Typography variant="h6" color="info.main">
                            {data.portfolioName}
                          </Typography>
                          {data?.facilityTabs?.map((facility, index) => {
                            const disabledMessage = tabDisabledMessage(index);
                            return (
                              <Tab
                                key={facility.sellerFacilityId}
                                style={{ border: 0, width: '100%', wordBreak: 'break-word' }}
                                disabled={!!disabledMessage}
                              >
                                <Tooltip
                                  placement="right"
                                  arrow
                                  title={disabledMessage}
                                  disableHoverListener={!disabledMessage}
                                >
                                  <span>{facility?.facilityName}</span>
                                </Tooltip>
                              </Tab>
                            );
                          })}
                        </Card>
                      )}
                      <Card sx={cardStyle}> Collaborator </Card>
                    </TabList>
                  </Grid>

                  <Grid item xs={12} sm={12} md={10}>
                    {data?.facilityTabs?.map((facility) => (
                      <TabPanel key={facility.sellerFacilityId}>
                        <>
                          {facility.sellerFacilityId === data.dueDiligenceFields.sellerFacilityId && (
                            <Grid container spacing={2} sx={{ height: PAGE_HEIGHT }}>
                              <Grid
                                item
                                xs={12}
                                sm={12}
                                md={10}
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Box sx={{ flexShrink: 0 }}>
                                  <CanceledAlert />
                                </Box>

                                <Box sx={{ flexShrink: 0 }}>
                                  <FormsDropdown formName="Due Diligence" />
                                </Box>

                                <Box
                                  className="item-scrollbar"
                                  onScroll={onScroll}
                                  ref={containerRef}
                                  sx={{
                                    flexGrow: 1,
                                    minHeight: 0,
                                    pb: facility.isViewable ? `calc(${PAGE_HEIGHT} - 280px)` : '',
                                    overflowY: 'auto',
                                  }}
                                >
                                  <>
                                    {isLoadingForms ? (
                                      <SkeletonBlock height={600} sx={{ mt: 2 }} />
                                    ) : facility.isViewable ? (
                                      <>
                                        {DueDiligenceFormTitles?.map((form, formIdx) => (
                                          <Card ref={formRefs[formIdx]} key={formIdx} sx={formCardStyle}>
                                            <FormBuilder
                                              formName={form.title}
                                              fields={data.dueDiligenceFields[form.fieldsTitle]}
                                            />
                                          </Card>
                                        ))}
                                      </>
                                    ) : (
                                      <AccessDenied />
                                    )}
                                  </>
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={12} md={2} sx={{ height: '100%' }}>
                                <Box
                                  className="hover-scrollbar"
                                  sx={{ height: '100%', overflowY: 'auto', width: '100%' }}
                                >
                                  <Card sx={cardStyle}>
                                    <SaveButton />
                                    <UpdateStatusButtons
                                      data={data}
                                      setData={setData}
                                      proceedButtonLabel="Begin Transition"
                                    />
                                  </Card>

                                  {facility.isViewable && (
                                    <TableOfContents activeSection={activeSection} formRefs={formRefs} />
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      </TabPanel>
                    ))}
                  </Grid>
                </Grid>
              </Tabs>
            </Grid>
          </form>
        </FormProvider>
      )}
    </>
  );
}
